/**
 * Root Application Component — Academic Curator
 *
 * Routing hierarchy:
 *   Admin: /entities → /entities/:id/students → /entities/:id/students/:sid → contacts
 *   Staff: /entities/:id/students (scoped to own entity) → ...
 *   Student: /profile (own) → /contacts (own)
 */

import { useRef, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from 'react-oidc-context';
import { User, BookOpen, Building2, GraduationCap, LogOut, AlertTriangle, Menu, X } from 'lucide-react';
import { EntityPickerPage } from './pages/EntityPickerPage';
import { StudentsListPage } from './pages/StudentsListPage';
import { ProfilePage } from './pages/ProfilePage';
import { ContactsListPage } from './pages/ContactsListPage';
import { ContactDetailPage } from './pages/ContactDetailPage';
import { ContactSlideOver, type EditingContact } from './components/ContactSlideOver';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { MockAuthProvider, useAppAuth, useIsAdmin, useIsAdminOrStaff, useIsStudentOnly } from '@/lib/mock-auth';
import { EntityProvider } from '@/lib/EntityContext';

const isMockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';

const oidcConfig = {
  authority: import.meta.env.VITE_COGNITO_AUTHORITY || 'https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_PLACEHOLDER',
  client_id: import.meta.env.VITE_COGNITO_CLIENT_ID || 'placeholder-client-id',
  redirect_uri: import.meta.env.VITE_REDIRECT_URI || window.location.origin + '/callback',
  response_type: 'code',
  scope: 'openid',
  post_logout_redirect_uri: window.location.origin,
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, '/');
  },
};

/** Cognito doesn't support standard OIDC end_session_endpoint — use its /logout directly */
function cognitoSignOut() {
  const domain = import.meta.env.VITE_COGNITO_DOMAIN;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  // Always clear local OIDC session state first
  sessionStorage.clear();
  if (domain && clientId) {
    // Cognito requires logout_uri to exactly match a registered logoutUrl.
    // The infra registers the origin WITH a trailing slash, so append one.
    const logoutUri = encodeURIComponent(window.location.origin + '/');
    window.location.href = `${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
  } else {
    window.location.href = '/';
  }
}

const MAX_REDIRECT_ATTEMPTS = 3;

/** Handles the OIDC callback — waits for token exchange then redirects */
function CallbackPage() {
  const auth = useAppAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      navigate('/', { replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  if (auth.error) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        Login failed: {auth.error.message}
      </div>
    );
  }

  return <div className="flex items-center justify-center h-64 text-muted-foreground">Processing login...</div>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const auth = useAppAuth();
  const redirectAttempts = useRef(0);
  const redirecting = useRef(false);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !redirecting.current) {
      if (redirectAttempts.current < MAX_REDIRECT_ATTEMPTS) {
        redirecting.current = true;
        redirectAttempts.current++;
        auth.signinRedirect();
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error]);

  if (auth.isLoading) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground" role="status" aria-live="polite">Loading authentication...</div>;
  }

  if (auth.error) {
    return (
      <Card className="max-w-md mx-auto mt-20">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-4" />
          <h2 className="font-display text-lg font-semibold mb-2">Authentication Error</h2>
          <p className="text-sm text-muted-foreground mb-4">{auth.error.message}</p>
          <Button variant="outline" onClick={() => { redirectAttempts.current = 0; redirecting.current = false; auth.signinRedirect(); }}>Try Again</Button>
        </CardContent>
      </Card>
    );
  }

  if (!auth.isAuthenticated) {
    if (redirectAttempts.current >= MAX_REDIRECT_ATTEMPTS) {
      return (
        <Card className="max-w-md mx-auto mt-20">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-4" />
            <h2 className="font-display text-lg font-semibold mb-2">Unable to Authenticate</h2>
            <p className="text-sm text-muted-foreground mb-4">Too many redirect attempts.</p>
            <Button variant="outline" onClick={() => { redirectAttempts.current = 0; redirecting.current = false; auth.signinRedirect(); }}>Retry Login</Button>
          </CardContent>
        </Card>
      );
    }
    return <div className="flex items-center justify-center h-64 text-muted-foreground" role="status" aria-live="polite">Redirecting to login...</div>;
  }

  redirectAttempts.current = 0;
  redirecting.current = false;
  return <>{children}</>;
}

function Shell() {
  const auth = useAppAuth();
  const isAdmin = useIsAdmin();
  const isAdminOrStaff = useIsAdminOrStaff();
  const isStudentOnly = useIsStudentOnly();
  const location = useLocation();
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<EditingContact | undefined>();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [contactsRefreshKey, setContactsRefreshKey] = useState(0);

  // Close mobile nav on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const openAddContact = () => { setEditingContact(undefined); setSlideOverOpen(true); };
  const openEditContact = (contact: EditingContact) => { setEditingContact(contact); setSlideOverOpen(true); };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const mockUser = auth.user?.profile;
  const entityId = mockUser?.['custom:entityId'] as string | undefined;

  // Role-based navigation
  const nav = isAdmin
    ? [{ to: '/entities', label: 'Entities', icon: Building2 }]
    : isAdminOrStaff && entityId
      ? [{ to: `/entities/${entityId}/students`, label: 'Students', icon: Building2 }]
      : [
          { to: '/profile', label: 'My Profile', icon: User },
          { to: '/contacts', label: 'My Contacts', icon: BookOpen },
        ];

  // Role-based default route
  const defaultRoute = isAdmin
    ? '/entities'
    : isAdminOrStaff && entityId
      ? `/entities/${entityId}/students`
      : '/profile';

  const groups = (mockUser?.['custom:groups'] as string[]) ?? [];
  const roleLabel = groups.includes('admin') ? 'Admin' : groups.includes('staff') ? 'Staff' : 'Student';

  return (
    <div className="flex min-h-screen bg-background">
      {isMockAuth && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-primary text-primary-foreground text-center text-[10px] py-1 font-bold uppercase tracking-widest">
          Mock Auth — {mockUser?.given_name} {mockUser?.family_name} ({roleLabel})
        </div>
      )}

      {/* Mobile top bar */}
      <header className={cn(
        'lg:hidden fixed top-0 left-0 right-0 z-40 glass flex items-center justify-between px-4 py-3 shadow-sm',
        isMockAuth && 'top-6'
      )}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-white shadow-sm" aria-hidden="true">
            <GraduationCap className="w-4 h-4" />
          </div>
          <span className="font-black tracking-tighter text-primary text-sm">Academic Curator</span>
        </div>
        <button
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          className="p-2 hover:bg-surface-low rounded-xl transition-colors"
          aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileNavOpen}
          aria-controls="mobile-nav"
        >
          {mobileNavOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
        </button>
      </header>

      {/* Mobile nav overlay */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 bg-slate-900/30 backdrop-blur-[1px] z-30" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
      )}

      {/* Sidebar — hidden on mobile, slide-in when open */}
      <aside
        id="mobile-nav"
        aria-label="Main navigation"
        className={cn(
          'w-64 shrink-0 glass fixed inset-y-0 left-0 z-30 flex flex-col shadow-2xl shadow-slate-200/50 transition-transform duration-300',
          'lg:translate-x-0',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
          isMockAuth && 'top-6'
        )}
      >
        <div className="flex items-center gap-3 px-6 py-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-white shadow-lg shadow-primary/20" aria-hidden="true">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-black tracking-tighter text-primary leading-none text-sm">Academic Curator</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-0.5">{roleLabel}</p>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-1" aria-label="Primary">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200',
                isActive(to)
                  ? 'bg-gradient-to-r from-primary to-indigo-500 text-white shadow-lg shadow-indigo-200/50 font-medium'
                  : 'text-muted-foreground hover:bg-surface-low hover:text-foreground font-medium'
              )}
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 mt-auto">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold" aria-hidden="true">
              {(mockUser?.given_name as string)?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-foreground truncate">
                {mockUser?.given_name as string || ''} {mockUser?.family_name as string || ''}
              </p>
              <p className="text-[10px] text-muted-foreground truncate">{mockUser?.email as string || 'User'}</p>
            </div>
          </div>
          <button
            onClick={() => isMockAuth ? auth.signoutRedirect() : cognitoSignOut()}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-muted-foreground hover:bg-surface-low hover:text-foreground transition-all w-full font-medium"
            aria-label="Sign out of your account"
          >
            <LogOut className="w-5 h-5" aria-hidden="true" /> Sign Out
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        <main className={cn(
          'pt-16 pb-12 px-4 sm:px-6 lg:pt-8 lg:px-12',
          isMockAuth && 'pt-[88px] lg:pt-14'
        )}>
          <Routes>
            <Route path="/entities" element={<ProtectedRoute><EntityPickerPage /></ProtectedRoute>} />
            <Route path="/entities/:entityId/students" element={<ProtectedRoute><StudentsListPage /></ProtectedRoute>} />
            <Route path="/entities/:entityId/students/:studentId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/entities/:entityId/students/:studentId/contacts" element={<ProtectedRoute><ContactsListPage onAddContact={openAddContact} onEditContact={openEditContact} refreshKey={contactsRefreshKey} /></ProtectedRoute>} />
            <Route path="/entities/:entityId/students/:studentId/contacts/:typeCategory" element={<ProtectedRoute><ContactDetailPage onAddContact={openAddContact} onEditContact={openEditContact} /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><ContactsListPage onAddContact={openAddContact} onEditContact={openEditContact} refreshKey={contactsRefreshKey} /></ProtectedRoute>} />
            <Route path="/callback" element={<CallbackPage />} />
            <Route path="/" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
        </main>
      </div>

      <ContactSlideOver open={slideOverOpen} onClose={() => setSlideOverOpen(false)} onSaved={() => setContactsRefreshKey((k) => k + 1)} contact={editingContact} />
    </div>
  );
}

function App() {
  if (isMockAuth) {
    return (
      <MockAuthProvider>
        <EntityProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </EntityProvider>
      </MockAuthProvider>
    );
  }

  return (
    <AuthProvider {...oidcConfig}>
      <EntityProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </EntityProvider>
    </AuthProvider>
  );
}

export default App;
